import type { UserGamificationProfile } from '../types';
import { BADGE_DEFINITIONS, STYLE_LEVELS } from '../stores/gamificationStore';

const API_BASE = process.env.REACT_APP_API_URL || 'https://a3d1kmonz2.execute-api.us-east-1.amazonaws.com/dev';

/**
 * Gamification service — API calls for user gamification profile.
 * Falls back to a default profile when the backend endpoints are not yet deployed.
 * Uses direct fetch (no retries) to avoid noisy console errors for undeployed endpoints.
 */
class GamificationService {
  async fetchProfile(userId: string): Promise<UserGamificationProfile> {
    try {
      const res = await fetch(`${API_BASE}/gamification/profile/${userId}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });
      if (!res.ok) throw new Error(`${res.status}`);
      return await res.json();
    } catch {
      return this.getDefaultProfile(userId);
    }
  }

  async updateStreak(userId: string): Promise<void> {
    try {
      await fetch(`${API_BASE}/gamification/streak`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId }),
      });
    } catch { /* best-effort */ }
  }

  async awardBadge(userId: string, badgeId: string): Promise<void> {
    try {
      await fetch(`${API_BASE}/gamification/badge`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId, badgeId }),
      });
    } catch { /* best-effort */ }
  }

  /**
   * Build a profile from the user's actual activity data.
   * Awards badges based on real upload counts, rating counts, etc.
   * Resets monthly — badges are for the current month's "Drip Challenge".
   */
  buildProfileFromActivity(
    userId: string,
    uploadCount: number,
    ratingsGiven: number = 0,
    consecutiveDays: number = 0,
  ): UserGamificationProfile {
    const now = new Date();
    const monthKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    const timestamp = now.toISOString();

    const badges = BADGE_DEFINITIONS.map((b) => {
      let earned = false;
      if (b.id === 'first-upload' && uploadCount >= 1) earned = true;
      if (b.id === 'ten-ratings' && ratingsGiven >= 10) earned = true;
      if (b.id === 'fifty-ratings' && ratingsGiven >= 50) earned = true;
      if (b.id === 'seven-day-streak' && consecutiveDays >= 7) earned = true;
      if (b.id === 'thirty-day-streak' && consecutiveDays >= 30) earned = true;
      return { ...b, earnedAt: earned ? timestamp : undefined };
    });

    // Points: 50 per badge + 10 per upload + 2 per rating
    const earnedBadgeCount = badges.filter((b) => b.earnedAt).length;
    const totalPoints = earnedBadgeCount * 50 + uploadCount * 10 + ratingsGiven * 2;

    let styleLevel = STYLE_LEVELS[0];
    for (const l of STYLE_LEVELS) {
      if (totalPoints >= l.minPoints) styleLevel = l;
    }

    return {
      userId,
      styleLevel,
      badges,
      streak: {
        currentStreak: consecutiveDays,
        longestStreak: consecutiveDays,
        lastActivityDate: timestamp,
        isBroken: false,
      },
      totalPoints,
    };
  }

  private getDefaultProfile(userId: string): UserGamificationProfile {
    return {
      userId,
      styleLevel: STYLE_LEVELS[0],
      badges: BADGE_DEFINITIONS.map((b) => ({ ...b })),
      streak: { currentStreak: 0, longestStreak: 0, lastActivityDate: new Date().toISOString(), isBroken: false },
      totalPoints: 0,
    };
  }
}

export default new GamificationService();
